import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"

dayjs.extend(isoWeek)

/**
 * 获取最近几天的日期范围
 * @param param0
 * @returns
 */
export function getLastXDaysRange({ dayRange = 7, format = "YYYY-MM-DD" }) {
  const currentDate = dayjs()
  const computedDate = dayjs().subtract(dayRange, "days")
  return [computedDate.format(format), currentDate.format(format)] as [string, string]
}

export function getBusinessDateTimeRange(dateRange: [string, string], businessTime: string) {
  const startTime = businessTime || "00:00:00"
  const [startDate, endDate] = dateRange
  return [
    dayjs(`${startDate} ${startTime}`).format("YYYY-MM-DD HH:mm:ss"),
    dayjs(`${endDate} ${startTime}`).add(1, "day").format("YYYY-MM-DD HH:mm:ss"),
  ] as [string, string]
}

export function formatBusinessSettlementRangeLabel(businessTime: string) {
  const time = (businessTime || "00:00:00").slice(0, 5)
  return `${time}-次日${time}`
}

/**
 * 根据分钟数计算小时数和分钟数
 * @param minutes 分钟数
 * @returns 小时数和分钟数
 */
export const computedMinutes = (minutes: number) => {
  // 传入的minutes可能由类似于 0.1+0.2等浮点运算得出的值，可能有精度问题
  return `${Math.floor(minutes / 60)}小时${Math.floor(minutes % 60)}分钟`
}

/**
 * 快捷日期
 */
export const shortcuts = [
  {
    text: "今日",
    key: "today",
    value: () => {
      const start = dayjs().startOf('day')
      const end = dayjs().endOf('day')
      return [start.toDate(), end.toDate()]
    },
  },
  {
    text: "昨日",
    key: "yesterday",
    value: () => {
      const start = dayjs().subtract(1, "days").startOf('day')
      const end = dayjs().subtract(1, "days").startOf('day')
      return [start.toDate(), end.toDate()]
    },
  },
  {
    text: "本周",
    key: 'thisweek',
    value: () => {
      const start = dayjs().startOf('isoWeek') // 开始日期为本周一
      const end = dayjs().endOf('isoWeek') // 结束日期为本周日
      return [start.toDate(), end.toDate()]
    },
  },
  {
    text: "本月",
    key: 'thismonth',
    value: () => {
      const end = dayjs().endOf('month')
      const start = dayjs().startOf('month')
      return [start.toDate(), end.toDate()]
    },
  },
  {
    text: "上月",
    key: 'lastmonth',
    value: () => {
      const end = dayjs().subtract(1, 'month').endOf('month')
      const start = dayjs().subtract(1, 'month').startOf('month')
      return [start.toDate(), end.toDate()]
    },
  },
]

export const futureShortcuts = [
  {
    text: "明日",
    key: 'tomorrow',
    value: () => {
      const start = dayjs().add(1, "days").startOf('day')
      const end = dayjs().add(1, "days").endOf('day')
      return [start.toDate(), end.toDate()]
    },
  },
  {
    text: "下周",
    key: 'nextweek',
    value: () => {
      const start = dayjs().add(1, "weeks").startOf('isoWeek')
      const end = dayjs().add(1, "weeks").endOf('isoWeek')
      return [start.toDate(), end.toDate()]
    },
  },
  {
    text: "下个月",
    key: 'nextmonth',
    value: () => {
      const start = dayjs().add(1, "months").startOf('month')
      const end = dayjs().add(1, "months").endOf('month')
      return [start.toDate(), end.toDate()]
    },
  },
]

// 获取近30天的日期范围
export const getRecent30DaysRange = (format = "YYYY-MM-DD"): [string, string] => {
  const start = dayjs().subtract(30, "days").startOf("day")
  const end = dayjs().endOf("day")
  return [start.format(format), end.format(format)]
}

/**
 * 获取当月的所有日期
 * @param format 格式
 * @returns
 */
export function getTwoDatesFromStartOfMonth(format = "YYYY-MM-DD"): [string, string] {
  const today = dayjs().format(format)
  const startOfMonth = dayjs().startOf("month").format(format)
  return [startOfMonth, today] as [string, string]
}
/**
 * 获取当天的日期范围
 * @param format 格式
 * @returns
 */
export function getTodayRange(format = "YYYY-MM-DD"): [string, string] {
  const today = dayjs().format(format)
  return [today, today]
}

/**
 * 日期范围默认值
 */
export const DEFAULT_REQUIRED_TIMERANGE = ["2000-01-01", "2099-12-31"]

export const getShortDate = (date: string | Date, dateFormat?: string, timeFormat?: string) => {
  const givenDate = dayjs(date)
  const today = dayjs().startOf('day')
  const yesterday = dayjs().subtract(1, 'days').startOf('day')
  if (givenDate.isSame(today, 'day')) {
    return '今天' + (timeFormat ? ` ${givenDate.format(timeFormat)}` : '')
  } else if (givenDate.isSame(yesterday, 'day')) {
    return '昨天' + (timeFormat ? ` ${givenDate.format(timeFormat)}` : '')
  } else {
    return givenDate.format(dateFormat) + (timeFormat ? ` ${givenDate.format(timeFormat)}` : '')
  }
}

/**
 * 预订预抵时间的相对日期文案。
 * 今日/昨日/明日使用短标签，其他日期回退到月日时间，避免把未来日期误显示为今日。
 */
export const getReservationArrivalText = (
  date?: string | Date | null,
  dateFormat = "MM-DD",
  timeFormat = "HH:mm",
) => {
  if (!date) {
    return "--"
  }

  const givenDate = dayjs(date)
  if (!givenDate.isValid()) {
    return "--"
  }

  const today = dayjs().startOf("day")
  const yesterday = today.subtract(1, "day")
  const tomorrow = today.add(1, "day")
  const timeText = givenDate.format(timeFormat)

  if (givenDate.isSame(today, "day")) {
    return `今日 ${timeText}`
  }

  if (givenDate.isSame(yesterday, "day")) {
    return `昨日 ${timeText}`
  }

  if (givenDate.isSame(tomorrow, "day")) {
    return `明日 ${timeText}`
  }

  return `${givenDate.format(dateFormat)} ${timeText}`
}
